import { observer } from 'mobx-react-lite';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';

import { CommonLoader } from '~components/Loaders/CommonLoader';
import { FeatureFlags } from '~constants/featureFlags';
import ApproveJobsFiles from '~pages/Approvals/Files/Files';
import useJobQuery from '~queries/useJobQuery';
import { ProtectedRoute } from '~router/ProtectedRoute';
import { ROUTES } from '~router/routePaths';
import { useStores } from '~store';
import { useAwaitableFeatureFlag } from '~utils/hooks/useFeatureFlag';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

interface ApproveJobsRedirectProps {
  canApproveJob: boolean;
}

export default observer(function ApproveJobsRedirect({
  canApproveJob,
}: ApproveJobsRedirectProps) {
  const { jobId } = useParams();
  const [searchParams] = useSearchParams();
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const { isAwaitingTreatment, isFeatureFlagEnabled: chargeBasedSettlements } =
    useAwaitableFeatureFlag({ featureFlagKey: FeatureFlags.chargeBasedSettlements });
  const jobIdToCarry = chargeBasedSettlements && canApproveJob ? jobId : undefined;
  const { isLoading: isResolvingJob, job } = useJobQuery(companyId, jobIdToCarry);

  if (isAwaitingTreatment) {
    return <CommonLoader />;
  }

  if (!chargeBasedSettlements) {
    return <ProtectedRoute element={ApproveJobsFiles} canAccess={canApproveJob} />;
  }

  if (isResolvingJob) {
    return <CommonLoader />;
  }

  const approveChargesPath = `/${ROUTES.approvals.base}/${ROUTES.approvals.approveCharges}`;
  const searchTerm = jobId ? job?.jobId : searchParams.get('search');

  return (
    <Navigate
      to={
        searchTerm
          ? `${approveChargesPath}?search=${encodeURIComponent(searchTerm)}`
          : approveChargesPath
      }
      replace
    />
  );
});
